import { useState, useCallback, useEffect } from "react";
import { useAuth } from "src/context/AuthContext";
import { BookingFormData } from "../types";
import { StudentParentFormData } from "src/components/forms/StudentParentForm";
import { useTimezone } from "src/hooks/useTimezone";

export const useBookingForm = () => {
  const { session } = useAuth();
  const { detectedTimezone } = useTimezone();
  
  const [formData, setFormData] = useState<BookingFormData>({
    timeSlot: "",
    duration: "60",
    topic: "",
    notes: "",
    timezone: detectedTimezone || "",
  });

  const [userFormData, setUserFormData] = useState<StudentParentFormData>({
    parentName: "",
    studentName: "",
    parentPhone: "",
    email: "",
  });

  // Update timezone when detected timezone changes
  useEffect(() => {
    if (detectedTimezone && !formData.timezone) {
      setFormData(prev => ({ ...prev, timezone: detectedTimezone }));
    }
  }, [detectedTimezone, formData.timezone]);

  // Pre-fill user info if logged in
  useEffect(() => {
    if (session?.user) {
      setUserFormData(prev => ({
        ...prev,
        email: session.user.email || "",
      }));
    }
  }, [session]);

  const updateFormData = useCallback((updates: Partial<BookingFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  const updateUserFormData = useCallback((updates: Partial<StudentParentFormData>) => {
    setUserFormData(prev => ({ ...prev, ...updates }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      timeSlot: "",
      duration: "60",
      topic: "",
      notes: "",
      timezone: detectedTimezone || "",
    });
    setUserFormData({
      parentName: "",
      studentName: "",
      parentPhone: "",
      email: "",
    });
  }, [detectedTimezone]);

  return {
    formData,
    userFormData,
    updateFormData,
    updateUserFormData,
    resetForm,
  };
};